import React, { useRef, useState } from 'react';

const BRAND = {
  navy: '#1A2744',
  blue: '#2563EB',
  lightBlue: '#EFF6FF',
  green: '#16A34A',
  lightGreen: '#DCFCE7',
  gray: '#6B7280',
  lightGray: '#F9FAFB',
  border: '#E5E7EB',
};

const FRAMEWORK_ITEMS = [
  { section: 'Keyword Strategy', color: '#185FA5', bg: '#E6F1FB', items: ['Services and/or product keywords discussed', 'Sector keywords (if applicable)', 'Location keywords (if applicable)', 'Blog posts / objections / pain points / FAQs / hub and spoke', 'Competitive analysis reviewed'] },
  { section: 'SEO Technical Fundamentals', color: '#27500A', bg: '#EAF3DE', items: ['SEO audit and fixes explained', 'Metadata updates discussed', 'Content development plan outlined'] },
  { section: 'Authority Levels / Google Trust / Domain Authority', color: '#633806', bg: '#FAEEDA', items: ['Backlink acquisition strategy explained', 'Citation development discussed', 'Press releases and other link types covered'] },
  { section: 'Lead Generation Principles', color: '#26215C', bg: '#EEEDFE', items: ['Conversion rate optimisation discussed', 'Landing page improvements reviewed', 'Messaging / offer improvements explained'] },
  { section: 'Ongoing Process', color: '#1A2744', bg: '#F1F5F9', items: ['Continuous improvements through testing and measuring explained', 'Detailed monthly reporting process confirmed'] },
];

const ACCESS_LABELS = { cms: 'CMS / Website', ga: 'Google Analytics', gsc: 'Google Search Console', gtm: 'Google Tag Manager', gbp: 'Google Business Profile' };
const ACCESS_STATUS = { granted: '✓ Access granted', pending: '⏳ Pending', create: '🔧 Need to create', na: '— N/A' };
const ACCESS_COLORS = { granted: '#16A34A', pending: '#D97706', create: '#2563EB', na: '#9CA3AF' };

function DocSection({ title, color, children }) {
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: color || BRAND.navy, marginBottom: '0.75rem', paddingBottom: 6, borderBottom: `2px solid ${color || BRAND.blue}`, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  if (!value || !String(value).trim()) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.gray, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#1F2937', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  );
}

function Grid2({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' }}>{children}</div>;
}

export default function IntakePreview({ data, strategySummary, generating, onBack, onSave, saving, saveSuccess }) {
  const { clientName, completedBy, docsReviewed, docsNotes, frameworkChecked, access, accessNotes, nap, survey } = data;
  const printRef = useRef();
  const [editableSummary, setEditableSummary] = useState(strategySummary);

  // Keep in sync if prop updates (e.g. still generating)
  React.useEffect(() => { setEditableSummary(strategySummary); }, [strategySummary]);

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>SEO Kick-Off — ${clientName}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif; color: #111827; background: #fff; font-size: 13px; } .no-print { display: none !important; } .print-only { display: block !important; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } @page { margin: 1.5cm; size: A4; } }
    </style></head><body>${printRef.current.innerHTML}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const checkedCount = Object.values(frameworkChecked).filter(Boolean).length;
  const totalFramework = FRAMEWORK_ITEMS.reduce((a, s) => a + s.items.length, 0);

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 10 }}>
        <button className="btn btn-sm" onClick={onBack}>← Edit form</button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-sm" onClick={onSave} disabled={saving}>
            {saving ? <><span className="spinner" /> Saving…</> : saveSuccess ? '✓ Saved!' : '💾 Save'}
          </button>
          <button className="btn btn-primary" onClick={handlePrint}>🖨️ Print / Save as PDF</button>
        </div>
      </div>

      {generating && (
        <div className="alert alert-info" style={{ marginBottom: 12 }}>
          <span className="spinner" style={{ marginRight: 8 }} /> Generating strategy summary…
        </div>
      )}

      {/* Document */}
      <div ref={printRef} style={{ background: '#fff', fontFamily: "-apple-system, 'Helvetica Neue', Arial, sans-serif" }}>

        {/* Header */}
        <div style={{ background: BRAND.navy, padding: '2rem 2.5rem', borderRadius: '12px 12px 0 0', marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: BRAND.blue, borderRadius: 8, padding: '5px 12px', marginBottom: 14 }}>
                <div style={{ width: 7, height: 7, background: '#60A5FA', borderRadius: '50%' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: '0.03em' }}>IMPROVE MY SEARCH RANKING</span>
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: 6 }}>SEO Kick-Off Document</div>
              <div style={{ fontSize: 16, color: '#93C5FD', fontWeight: 500 }}>{clientName}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#93C5FD', marginBottom: 3 }}>Date</div>
              <div style={{ fontSize: 13, color: '#fff', fontWeight: 600, marginBottom: 10 }}>{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              {completedBy && <>
                <div style={{ fontSize: 11, color: '#93C5FD', marginBottom: 3 }}>Completed by</div>
                <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{completedBy}</div>
              </>}
            </div>
          </div>

          {/* Docs reviewed badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {docsReviewed.keywordResearch && <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 12, padding: '4px 12px', borderRadius: 99, fontWeight: 500 }}>✓ Keyword research reviewed</span>}
            {docsReviewed.seoAudit && <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 12, padding: '4px 12px', borderRadius: 99, fontWeight: 500 }}>✓ SEO audit reviewed</span>}
            <span style={{ background: 'rgba(255,255,255,0.1)', color: '#93C5FD', fontSize: 12, padding: '4px 12px', borderRadius: 99 }}>Framework: {checkedCount}/{totalFramework} points covered</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '2rem 2.5rem', background: '#fff', border: `1px solid ${BRAND.border}`, borderTop: 'none', borderRadius: '0 0 12px 12px' }}>

          {/* Strategy summary */}
          {editableSummary && (
            <div style={{ background: BRAND.lightBlue, borderRadius: 10, padding: '1.25rem 1.5rem', marginBottom: '2rem', borderLeft: `4px solid ${BRAND.blue}` }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: BRAND.blue, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>SEO Strategy Summary</div>
              <textarea
                value={editableSummary}
                onChange={e => setEditableSummary(e.target.value)}
                rows={8}
                className="no-print"
                style={{ width: '100%', border: '1px solid #BFDBFE', borderRadius: 6, padding: '8px 10px', fontSize: 13, color: '#1E3A5F', lineHeight: 1.75, resize: 'vertical', fontFamily: 'inherit', background: 'rgba(255,255,255,0.7)' }}
              />
              <div className="print-only" style={{ fontSize: 13, color: '#1E3A5F', lineHeight: 1.75, whiteSpace: 'pre-wrap', display: 'none' }}>{editableSummary}</div>
            </div>
          )}

          {/* Framework */}
          <DocSection title="Our Framework for SEO Success" color={BRAND.blue}>
            {FRAMEWORK_ITEMS.map((section, si) => (
              <div key={si} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: section.color, marginBottom: 6 }}>{section.section}</div>
                {section.items.map((item, ii) => {
                  const key = `${si}-${ii}`;
                  const checked = !!frameworkChecked[key];
                  return (
                    <div key={ii} style={{ display: 'flex', gap: 8, fontSize: 12, color: checked ? '#1F2937' : BRAND.gray, marginBottom: 4, alignItems: 'flex-start' }}>
                      <span style={{ color: checked ? BRAND.green : BRAND.gray, flexShrink: 0, fontWeight: 700 }}>{checked ? '✓' : '○'}</span>
                      <span style={{ textDecoration: checked ? 'none' : 'none' }}>{item}</span>
                    </div>
                  );
                })}
              </div>
            ))}
            {docsNotes && <div style={{ marginTop: 10, padding: '8px 12px', background: BRAND.lightGray, borderRadius: 6, fontSize: 12, color: '#374151' }}><strong>Document notes:</strong> {docsNotes}</div>}
          </DocSection>

          {/* Access details */}
          <DocSection title="Access Details" color={BRAND.green}>
            <div style={{ fontSize: 12, color: BRAND.gray, marginBottom: 10 }}>
              Access requested for: <strong style={{ color: BRAND.navy }}>josh@improvemysearchranking.com / joshanthonyhamit@gmail.com</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {Object.entries(ACCESS_LABELS).map(([key, label]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', borderRadius: 6, background: BRAND.lightGray, border: `1px solid ${BRAND.border}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.navy, marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 12, color: ACCESS_COLORS[access[key]] || BRAND.gray, fontWeight: 500 }}>{ACCESS_STATUS[access[key]] || '—'}</div>
                    {accessNotes[key] && <div style={{ fontSize: 11, color: BRAND.gray, marginTop: 2 }}>{accessNotes[key]}</div>}
                  </div>
                </div>
              ))}
            </div>
          </DocSection>

          {/* NAP */}
          <DocSection title="NAP Details — Name, Address & Phone" color='#D97706'>
            <Grid2>
              <Field label="Business name" value={nap.businessName} />
              <Field label="Owner's name" value={nap.ownerName} />
              <Field label="Phone number" value={nap.phone} />
              <Field label="Citation contact name" value={nap.citationContact} />
              <Field label="Founded year" value={nap.foundedYear} />
              <Field label="Opening hours" value={nap.openingHours} />
            </Grid2>
            <Field label="Business address" value={nap.address} />
            <Field label="Business description" value={nap.description} />
          </DocSection>

          {/* Content survey */}
          <DocSection title="SEO Content Survey" color={BRAND.navy}>

            <div style={{ fontSize: 12, fontWeight: 700, color: '#185FA5', marginBottom: 8, marginTop: 4 }}>About Your Business</div>
            <Field label="Main business focus" value={survey.mainFocus} />
            <Field label="Unique selling points" value={survey.usps} />
            <Field label="Seasonal trends" value={survey.seasonal} />

            <div style={{ fontSize: 12, fontWeight: 700, color: '#27500A', marginBottom: 8, marginTop: 14 }}>About Your Customers</div>
            <Grid2>
              <Field label="Age range" value={survey.ageRange} />
              <Field label="Gender" value={survey.gender} />
              <Field label="Occupation / industry" value={survey.occupation} />
              <Field label="Location" value={survey.location} />
            </Grid2>
            <Field label="Other demographics" value={survey.otherDemographics} />
            <Field label="Customer challenges solved" value={survey.customerChallenges} />

            <div style={{ fontSize: 12, fontWeight: 700, color: '#633806', marginBottom: 8, marginTop: 14 }}>Competitor & Market Insights</div>
            <Field label="Primary competitors" value={survey.competitors} />
            <Field label="Industry trends" value={survey.industryTrends} />

            <div style={{ fontSize: 12, fontWeight: 700, color: '#26215C', marginBottom: 8, marginTop: 14 }}>Content & Tone</div>
            <Field label="Brand voice" value={survey.brandVoice === 'other' ? survey.brandVoiceOther : survey.brandVoice} />
            <Field label="Language / topics to avoid" value={survey.avoidLanguage} />
            <Field label="Preferred keywords / style" value={survey.keywordsStyle} />

            <div style={{ fontSize: 12, fontWeight: 700, color: '#1A2744', marginBottom: 8, marginTop: 14 }}>Google E-E-A-T Signals</div>
            <Grid2>
              <Field label="Jobs / projects completed" value={survey.jobsCompleted} />
              <Field label="Notable clients" value={survey.notableClients} />
              <Field label="Qualifications & certifications" value={survey.qualifications} />
              <Field label="Subject matter experts" value={survey.subjectExperts} />
              <Field label="Original research / data" value={survey.originalResearch} />
              <Field label="Media / publication features" value={survey.publications} />
              <Field label="Partnerships & memberships" value={survey.partnerships} />
              <Field label="Awards & recognitions" value={survey.awards} />
            </Grid2>
            <Field label="Testimonials & reviews" value={survey.testimonials} />
            <Field label="Guarantees & trust policies" value={survey.guarantees} />
            <Field label="Compliance & accreditations" value={survey.compliance} />

            {survey.additionalNotes && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.gray, marginBottom: 6 }}>Additional Notes</div>
                <div style={{ padding: '10px 14px', background: BRAND.lightGray, borderRadius: 6, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{survey.additionalNotes}</div>
              </div>
            )}
          </DocSection>

          {/* Footer */}
          <div style={{ borderTop: `1px solid ${BRAND.border}`, paddingTop: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 11, color: BRAND.gray }}>Improve My Search Ranking · improvemysearchranking.com</div>
            <div style={{ fontSize: 11, color: BRAND.gray }}>SEO Kick-Off Document · {clientName} · {new Date().toLocaleDateString('en-GB')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
